import { useEffect, useState } from 'react';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { pathToPage, pageToPath } from './routing';
import type { AdminTab, Page, ProviderTab } from './routing';
import { Navbar } from './components/Navbar';
import { QuotePage } from './components/QuotePage';
import { ProviderPortal } from './components/ProviderPortal';
import { AuthPage } from './components/AuthPage';
import { ShopPage } from './components/ShopPage';
import { ProvidersPage } from './components/ProvidersPage';
import { ProductPage } from './components/ProductPage';
import { CartPage } from './components/CartPage';
import { OrdersPage } from './components/OrdersPage';
import { FavoritesPage } from './components/FavoritesPage';
import { AdminPanel } from './components/AdminPanel';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ProfileDrawer } from './components/ProfileDrawer';
import { ReferralLanding } from './components/ReferralLanding';
import { parseRefParam, stashRef } from './components/referralView';

export type { Page, AdminTab, ProviderTab } from './routing';

function Shell() {
  // 2f9c70ba: capture the ?ref=<code|slug> attribution token from the
  // landing URL before the SPA routing strips it. Stashed once, kept until
  // signup captures it — the URL itself is cleaned for shareability.
  useEffect(() => {
    const ref = parseRefParam(window.location.search);
    if (ref) {
      stashRef(window.localStorage, ref);
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, []);

  // TASK-cc4f60b3: initial page from the URL (email deep links /orders,
  // /provider, /admin/disputes); unknown paths fall back to the default
  // quote page, preserving the pre-routing first paint.
  const initialRoute = pathToPage(window.location.pathname);
  const [page, setPage] = useState<Page>(initialRoute.page);
  const [adminTab, setAdminTab] = useState<AdminTab | null>(initialRoute.adminTab);
  // F3 (268dfd51): provider tab from /provider/orders — actionable provider
  // notifications land on «Заказы», not the portal root. Same shape as
  // adminTab: consumed on mount (key + initialTab), cleared on client nav.
  const [providerTab, setProviderTab] = useState<ProviderTab | null>(initialRoute.providerTab);
  // 0e895f9a (a1a3b8f4 part 2): product edit target from /provider/products/new
  // or /provider/products/<guid> — the products tab opens the separate edit
  // page; consumed on mount, cleared on client nav (same shape as providerTab).
  const [providerProductId, setProviderProductId] = useState<string | null>(initialRoute.providerProductId);
  // s7 (e7fcdcbe): per-order Guid from /orders/:id — OrdersPage opens
  // focused on that order; consumed once, cleared on client-side nav.
  const [focusOrderId, setFocusOrderId] = useState<string | null>(initialRoute.orderId);
  // 2f9c70ba: referral token from /r/:code — consumed on mount; client-side
  // navigation away drops it (deep-link only surface).
  const [referralCode, setReferralCode] = useState<string | null>(initialRoute.referralCode);
  const [productId, setProductId] = useState<string | null>(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const { isAuthenticated, auth } = useAuth();

  useEffect(() => {
    const syncFromLocation = () => {
      const route = pathToPage(window.location.pathname);
      setPage(route.page);
      setAdminTab(route.adminTab);
      setProviderTab(route.providerTab);
      setProviderProductId(route.providerProductId);
      setFocusOrderId(route.orderId);
      setReferralCode(route.referralCode);
    };
    window.addEventListener('popstate', syncFromLocation);
    return () => window.removeEventListener('popstate', syncFromLocation);
  }, []);

  // Auth screen only for anonymous users; a restored session
  // falls through to the quote page instead.
  const showAuth = page === 'auth' && !isAuthenticated;

  const openProduct = (id: string) => {
    setProductId(id);
    setPage('product');
  };

  // s9 (d2de0c1c): optional nav context — a notification row tap can pass
  // the admin disputes tab and/or a per-order focus so the drawer lands on
  // the mapped surface, not just the page root.
  const navigate = (p: Page, opts?: { adminTab?: AdminTab; providerTab?: ProviderTab; focusOrderId?: string; providerProductId?: string }) => {
    if (p !== 'product') setProductId(null);
    setAdminTab(p === 'admin' ? opts?.adminTab ?? null : null);
    setProviderTab(p === 'provider' ? opts?.providerTab ?? null : null);
    setProviderProductId(p === 'provider' ? opts?.providerProductId ?? null : null);
    // Client-side navigation is list-intent — drop the per-order focus so
    // the orders page (re)mounts show the plain list — unless the caller
    // explicitly targets an order (s9 notification deep link).
    setFocusOrderId(p === 'orders' ? opts?.focusOrderId ?? null : null);
    // Same one-shot semantics for the referral landing token.
    setReferralCode(p === 'referral' ? referralCode : null);
    setPage(p);
    // /admin/disputes, /provider/orders and /provider/products/* are the only
    // sub-tabs with their own routes (email/deep links); notification taps land
    // on the same URL. Per-order focus keeps /orders (pageToPath never emits
    // per-order URLs).
    const path =
      p === 'admin' && opts?.adminTab === 'disputes'
        ? '/admin/disputes'
        : p === 'provider' && opts?.providerProductId
          ? `/provider/products/${opts.providerProductId}`
          : p === 'provider' && opts?.providerTab === 'orders'
            ? '/provider/orders'
            : pageToPath(p);
    if (window.location.pathname !== path) {
      window.history.pushState(null, '', path);
    }
  };

  return (
    <>
      <Navbar
        page={page}
        onNavigate={navigate}
        onOpenProfile={() => setProfileOpen(true)}
        onNotificationNavigate={(t) =>
          navigate(t.page, {
            adminTab: t.adminTab ?? undefined,
            providerTab: t.providerTab ?? undefined,
            focusOrderId: t.orderId ?? undefined,
          })
        }
      />
      <main>
        {showAuth ? (
          <AuthPage onDone={() => setPage('quote')} />
        ) : page === 'admin' && auth?.role === 'Admin' ? (
          <AdminPanel key={adminTab ?? 'users'} initialTab={adminTab ?? 'users'} />
        ) : page === 'provider' ? (
          <ProviderPortal
            key={`${providerTab ?? 'profile'}:${providerProductId ?? ''}`}
            initialTab={providerTab ?? 'profile'}
            initialProductEdit={providerProductId}
            onProductEditNavigate={(target) =>
              navigate('provider', { providerTab: 'products', providerProductId: target ?? undefined })
            }
          />
        ) : page === 'shop' ? (
          <ShopPage onOpenProduct={openProduct} />
        ) : page === 'referral' && referralCode ? (
          <ReferralLanding
            code={referralCode}
            onOpenProduct={openProduct}
            onGoToQuote={() => navigate('quote')}
          />
        ) : page === 'providers' ? (
          <ProvidersPage onOpenProduct={openProduct} />
        ) : page === 'product' && productId ? (
          <ProductPage
            productId={productId}
            onBack={() => navigate('shop')}
            onGoToCart={() => navigate('cart')}
            onNeedAuth={() => setPage('auth')}
          />
        ) : page === 'cart' ? (
          <CartPage onNeedAuth={() => setPage('auth')} />
        ) : page === 'orders' ? (
          <OrdersPage onNeedAuth={() => setPage('auth')} focusOrderId={focusOrderId} />
        ) : page === 'favorites' ? (
          <FavoritesPage onOpenProduct={openProduct} onNeedAuth={() => setPage('auth')} />
        ) : (
          <QuotePage onGoToCart={() => navigate('cart')} />
        )}
      </main>
      <ProfileDrawer open={profileOpen} onClose={() => setProfileOpen(false)} />
    </>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <Shell />
      </AuthProvider>
    </ErrorBoundary>
  );
}
